import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '../lib/api';
import { workOrderHref } from '../lib/desk';
import { formatRelative } from '../lib/format';
import type { AppNotification } from '../lib/types';
import { cn } from './ui';

/** In-app notifications from workflows and facilities work, shown in the sidebar. */
export function NotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const notifications = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<{ items: AppNotification[]; unread: number }>('/notifications'),
    refetchInterval: 60_000,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['notifications'] });
  const markRead = useMutation({ mutationFn: (id: string) => api.post(`/notifications/${id}/read`), onSuccess: () => void refresh() });
  const markAll = useMutation({ mutationFn: () => api.post('/notifications/read-all'), onSuccess: () => void refresh() });

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const unread = notifications.data?.unread ?? 0;
  const items = notifications.data?.items ?? [];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((value) => !value)}
        className="relative rounded-md p-1.5 text-brand-300 hover:bg-white/10 hover:text-white"
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-expanded={open}
      >
        <Bell className="size-4" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] leading-none font-semibold text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute bottom-full left-0 z-50 mb-2 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-slate-200 bg-white text-slate-800 shadow-xl">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
            <p className="text-sm font-semibold">Notifications</p>
            {unread > 0 && (
              <button onClick={() => markAll.mutate()} className="text-xs font-medium text-brand-700 hover:underline">
                Mark all read
              </button>
            )}
          </div>
          <ul className="max-h-96 divide-y divide-slate-100 overflow-y-auto">
            {items.length === 0 ? (
              <li className="px-4 py-6 text-center text-sm text-slate-500">You’re all caught up.</li>
            ) : (
              items.map((notification) => (
                <li key={notification.id}>
                  <button
                    onClick={() => {
                      if (!notification.readAt) markRead.mutate(notification.id);
                      setOpen(false);
                      if (notification.workOrder) navigate(workOrderHref(notification.workOrder.service, notification.workOrder.id));
                      else if (notification.inspection) navigate(`/facilities/inspections/${notification.inspection.id}`);
                    }}
                    className={cn('flex w-full gap-3 px-4 py-3 text-left hover:bg-slate-50', !notification.readAt && 'bg-brand-50/50')}
                  >
                    <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', notification.readAt ? 'bg-transparent' : 'bg-brand-600')} />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-slate-900">{notification.title}</span>
                      {notification.body && <span className="block truncate text-xs text-slate-500">{notification.body}</span>}
                      <span className="block text-xs text-slate-400">{formatRelative(notification.createdAt)}</span>
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
